export interface HowItWorksStep {
  title: string;
  description: string;
  details: string[];
}

export interface HowItWorksFaq {
  question: string;
  answer: string;
}

export const howItWorksContent = {
  title: 'How Event Finder Works',
  lastModified: '2026-08-18',
  description: 'Learn how Event Finder discovers public event listings, checks details, removes duplicates, improves locations, and keeps worldwide events searchable.',
  introduction: 'Event Finder turns public event listings into one searchable catalog. It looks for useful event websites, reads each listing, checks the details, and connects records that describe the same event.',
  steps: [
    {
      title: 'Find useful event websites',
      description: 'Event Finder searches for public event pages from organizations, corporations, communities, and cities.',
      details: [
        'It looks for maintained calendars and event lists, not just one-off pages.',
        'Possible sources are checked for a clear connection to the organization or place.',
      ],
    },
    {
      title: 'Read each event listing',
      description: 'The system first reads the page as structured text. If the useful details are only visible in the page layout, it can use a visual reading method instead.',
      details: [
        'Text-first reading is faster and preserves headings, links, and lists.',
        'Visual reading is a fallback for pages that do not expose enough useful text.',
      ],
    },
    {
      title: 'Extract the important details',
      description: 'Event Finder identifies facts people need to decide whether an event is relevant.',
      details: [
        'These facts can include the title, date, time, format, organizer, venue, description, and registration link.',
        'A field can stay empty when the source does not provide a reliable answer.',
      ],
    },
    {
      title: 'Check and combine records',
      description: 'Dates, links, and required fields are checked before an event is published. Exact and similar records are compared so one event is not shown many times.',
      details: [
        'Different titles can still be matched when the date, location, and meaning agree.',
        'Multiple source websites can remain connected to one event record.',
      ],
    },
    {
      title: 'Add useful context',
      description: 'Event Finder organizes reliable details so events can be searched and compared more easily.',
      details: [
        'Venue and city information support map and nearby-event searches.',
        'Format, topics, audience, and organization relationships may be added when there is enough evidence.',
      ],
    },
    {
      title: 'Publish and refresh',
      description: 'Approved events are added to the catalog and search index. Known event websites are revisited so newly published or changed events can be found.',
      details: [
        'Search, browse, city, organization, and map pages use the same event catalog.',
        'New source websites can be discovered while existing sources continue to be refreshed.',
      ],
    },
  ] as HowItWorksStep[],
  qualityNotes: [
    {
      title: 'Times follow the source',
      description: 'Event dates and times use the local wall-clock values published by the event website. A time-zone label is shown only when it can be derived reliably.',
    },
    {
      title: 'The original website remains important',
      description: 'Event details can change after they are collected. Check the linked source or registration page before making plans.',
    },
    {
      title: 'Unknown details are not guessed',
      description: 'A venue, price, format, or other field may be missing when the available source does not support a reliable value.',
    },
  ],
  faqs: [
    {
      question: 'Where does Event Finder get event information?',
      answer: 'Event Finder reads public event calendars and listings from organizations, corporations, communities, and cities. It keeps a link to the source website so visitors can confirm the latest details.',
    },
    {
      question: 'How does Event Finder reduce duplicate events?',
      answer: 'The system checks exact identifiers and compares titles, dates, locations, and meaning. When records describe the same event, their sources can be connected to one event instead of creating repeated listings.',
    },
    {
      question: 'How often are events updated?',
      answer: 'Known event websites are processed regularly, and newly accepted sources are queued for processing. Update timing varies by source, so the original event website is the final authority.',
    },
    {
      question: 'Does Event Finder cover virtual and worldwide events?',
      answer: 'Yes. The catalog includes virtual, in-person, and hybrid events from public sources around the world. Coverage depends on the sources that are available and can be read reliably.',
    },
  ] as HowItWorksFaq[],
} as const;